import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, KeyRound, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, errMsg } from '@/pres/api';
import { MapBg } from '@/pres/Chrome';
import { KraftBg, Stamp } from '@/pres/Dossier';

const field = 'w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 font-ui text-[15px] text-white outline-none placeholder:text-white/30 focus:border-blue-500 transition-colors';

export default function Home() {
  const nav = useNavigate();
  const [title, setTitle] = useState('Plateformes numériques');
  const [code, setCode] = useState('');
  const [code2, setCode2] = useState('');
  const [author, setAuthor] = useState('');
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState(false);
  const recent: { slug: string; title: string }[] = JSON.parse(localStorage.getItem('deck:recent') || '[]');

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length < 4) return toast.error('Le code de groupe doit faire au moins 4 caractères.');
    if (code !== code2) return toast.error('Les deux codes ne correspondent pas.');
    setBusy(true);
    try {
      const { slug } = await api.create(title, code);
      localStorage.setItem(`deck:${slug}:unlocked`, '1');
      if (author) localStorage.setItem('deck:author', author);
      nav(`/p/${slug}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const open = () => {
    const m = link.trim().match(/\/p\/([a-z0-9]+)/i);
    const slug = m ? m[1] : link.trim();
    if (slug) nav(`/p/${slug}`);
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0a0a0a] text-white">
      <div className="absolute inset-0 opacity-50" style={{ clipPath: 'polygon(0 0, 58% 0, 44% 100%, 0 100%)' }}><MapBg dark /></div>
      <div className="absolute inset-0 opacity-90" style={{ clipPath: 'polygon(58% 0, 100% 0, 100% 100%, 44% 100%)' }}><KraftBg /></div>
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/20" />
      <div className="relative z-10 mx-auto grid min-h-screen max-w-7xl grid-cols-1 gap-16 px-8 py-16 lg:grid-cols-[1.1fr_0.9fr] lg:px-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="flex flex-col justify-center gap-8">
          <div className="flex items-center gap-3 font-type text-sm uppercase tracking-[0.25em] text-white/60"><span className="h-2.5 w-2.5 rounded-full bg-blue-600" /><span className="h-2.5 w-2.5 bg-red-600" /> L1 Éco-Gestion · Exposé à trois</div>
          <h1 className="font-app text-4xl font-bold leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl">Plateformes numériques<span className="text-blue-500">.</span><br /><span className="font-elite font-normal text-[#e7d3ae]">Le dossier.</span></h1>
          <p className="max-w-xl font-ui text-base text-white/65">14 slides en 16:9, deux univers — l'appli pour l'axe I, le dossier judiciaire pour l'axe II. Zones vides à remplir à trois, sources vérifiables à côté de chaque extrait, mode présentateur et export PDF.</p>
          {recent.length > 0 && (
            <div className="flex flex-col gap-2" data-testid="recent-list">
              <span className="font-type text-xs uppercase tracking-[0.2em] text-white/40">Ouvertes récemment</span>
              {recent.map((r) => (
                <button key={r.slug} data-testid={`recent-${r.slug}`} onClick={() => nav(`/p/${r.slug}`)} className="group flex w-fit items-center gap-2 font-app text-lg text-white/85 hover:text-blue-400 transition-colors">
                  {r.title || 'Sans titre'} <span className="font-type text-xs text-white/35">/p/{r.slug}</span><ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </button>
              ))}
            </div>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="flex flex-col justify-center gap-6">
          <form onSubmit={create} className="glass-dark relative flex flex-col gap-4 rounded-3xl border border-white/10 p-8" data-testid="create-form">
            <Stamp className="absolute -right-4 -top-6 z-10 bg-transparent text-lg !mix-blend-normal" rotate={8} delay={0.6}>Nouveau dossier</Stamp>
            <h2 className="font-app text-lg font-semibold">Créer la présentation</h2>
            <input data-testid="create-title-input" className={field} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Nom interne (non affiché sur les slides)" />
            <input data-testid="create-author-input" className={field} value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="Votre prénom (pour « modifié par… »)" />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="create-code-input" type="password" className={field} value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code de groupe" />
              <input data-testid="create-code-confirm-input" type="password" className={field} value={code2} onChange={(e) => setCode2(e.target.value)} placeholder="Confirmer le code" />
            </div>
            <p className="flex items-center gap-2 font-ui text-xs text-white/45"><KeyRound size={14} /> Partagez le lien + le code à vos deux camarades. Sans le code : lecture seule.</p>
            <button data-testid="create-submit-button" disabled={busy} className="mt-2 flex items-center justify-center gap-2 rounded-full bg-blue-600 px-6 py-3 font-app font-semibold text-white hover:bg-blue-500 active:scale-[0.98] transition-[background-color,transform] disabled:opacity-50">
              {busy ? 'Création…' : 'Créer et ouvrir'} <ArrowRight size={18} />
            </button>
          </form>
          <div className="glass-dark flex items-center gap-3 rounded-2xl border border-white/10 p-3">
            <Link2 size={18} className="ml-2 text-white/40" />
            <input data-testid="open-link-input" className="flex-1 bg-transparent font-ui text-sm text-white outline-none placeholder:text-white/30" value={link} onChange={(e) => setLink(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && open()} placeholder="Coller le lien partagé…" />
            <button data-testid="open-link-button" onClick={open} className="rounded-full bg-white px-4 py-2 font-app text-sm font-semibold text-black hover:bg-[#e7d3ae] transition-colors">Ouvrir</button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
